import { PurchaseOrder, PODocument } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { FileText, Download, Eye, Clock, User, Package } from 'lucide-react';
import {
  formatFileSize,
  getDocumentTypeLabel,
  getDocumentTypeColor,
  groupDocumentsByType,
} from './PODetailHelpers';
import { usePODetailFormatters } from './usePODetailFormatters';
import FileDropZone from '../common/FileDropZone';

/** Types the PO document upload has always accepted. */
const PO_DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.csv';
const PO_DOCUMENT_HINT = 'PDF, Word, Excel, CSV or images';

interface PODetailDocumentsTabProps {
  po: PurchaseOrder;
  handleDocumentDownload: (doc: PODocument) => void;
  handleDocumentView: (doc: PODocument) => void;
  /** Receives the dropped / picked file (single file per upload). */
  onUploadFiles: (files: File[]) => void;
  uploading?: boolean;
  isInlineViewable: (doc: PODocument) => boolean;
}

/**
 * The Documents panel of the Purchase Order Details modal.
 *
 * Moved out of PODetailModal unchanged. The download / view / upload handlers
 * and the inline-viewable test stay in the parent (see usePODocumentActions)
 * because they need the modal's PO and refresh. Customer review 11 Sep: both
 * upload cards are the shared FileDropZone — they used to say "drag and drop"
 * with no drop handler behind them.
 */
export function PODetailDocumentsTab({
  po,
  handleDocumentDownload,
  handleDocumentView,
  onUploadFiles,
  uploading = false,
  isInlineViewable,
}: PODetailDocumentsTabProps) {
  const { formatDateTime } = usePODetailFormatters();

  return (
    <>
{po.documents && po.documents.length > 0 ? (
  <div className="space-y-6">
    {/* Upload Section */}
    <Card>
      <CardContent className="pt-6">
        <FileDropZone
          accept={PO_DOCUMENT_ACCEPT}
          busy={uploading}
          hint={PO_DOCUMENT_HINT}
          onFiles={onUploadFiles}
          data-testid="po-documents-drop-zone"
        />
      </CardContent>
    </Card>

    {/* Documents grouped by type */}
    {Object.entries(groupDocumentsByType(po.documents)).map(([type, docs]) => (
      <Card key={type}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle className="text-base">{getDocumentTypeLabel(type)}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {docs.length} {docs.length === 1 ? 'version' : 'versions'} available
                </p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDocumentTypeColor(type)}`}>
              {type}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {/* Version History */}
          <div className="space-y-3">
            {docs.map((doc, index) => (
              <div
                key={doc.id}
                className={`border rounded-lg p-4 ${index === 0 ? 'border-primary bg-primary/5' : 'border-border'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="font-medium text-sm truncate">
                        {doc.file_name || 'Untitled Document'}
                      </div>
                      {index === 0 && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                          Current
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDateTime(doc.upload_date)}</span>
                      </div>
                      {doc.uploaded_by_details && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>{doc.uploaded_by_details.full_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Package className="h-3 w-3" />
                        <span>{formatFileSize(doc.file_size)}</span>
                      </div>
                      <div className="text-muted-foreground">
                        Version {docs.length - index}
                      </div>
                    </div>

                    {doc.description && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        {doc.description}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* CR-5553 rework: View only for browser-
                        renderable types; others get Download
                        only (no more View-that-downloads). */}
                    {isInlineViewable(doc) && (
                      <button
                        onClick={() => handleDocumentView(doc)}
                        className="p-2 hover:bg-primary/10 rounded transition-colors"
                        title="View document"
                      >
                        <Eye className="h-4 w-4 text-primary" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDocumentDownload(doc)}
                      className="p-2 hover:bg-primary/10 rounded transition-colors"
                      title="Download document"
                    >
                      <Download className="h-4 w-4 text-primary" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
) : (
  <div className="space-y-6">
    {/* Upload Section - Empty State */}
    <Card>
      <CardContent className="pt-6">
        <FileDropZone
          accept={PO_DOCUMENT_ACCEPT}
          busy={uploading}
          hint={PO_DOCUMENT_HINT}
          onFiles={onUploadFiles}
          className="p-8"
          data-testid="po-documents-drop-zone"
        />
      </CardContent>
    </Card>

    {/* Empty State */}
    <div className="text-center py-12">
      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
      <p className="text-muted-foreground">No documents attached to this purchase order</p>
      <p className="text-sm text-muted-foreground mt-2">
        Upload documents such as purchase orders, certificates, or specifications
      </p>
    </div>
  </div>
)}
    </>
  );
}
